import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './ErrorBoundary.css';

/**
 * Error boundary component to catch and display errors in the UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * Update state when an error occurs
   * @param {Error} error - The error that was thrown
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  /**
   * Catch and log errors
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Component stack information
   */
  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // You could also log to an error reporting service here
  }

  /**
   * Reset the error state to try again
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;
    
    // If there's an error, show the error UI
    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback(error, this.handleReset);
      }
      
      // Otherwise, show the default error UI
      return (
        <div className="error-boundary">
          <div className="error-content">
            <h2>Something went wrong</h2>
            <div className="error-details">
              <p>{error && error.toString()}</p>
              <div className="error-stack">
                {errorInfo && errorInfo.componentStack}
              </div>
            </div>
            <button onClick={this.handleReset} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      );
    }

    // If no error, render the children normally
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func
};

export default ErrorBoundary;
